// Iter 81 / Phase E0 — Employee Operations Module · Directory List.
// Mirrors the VendorList layout: URL-state filters, sort, pagination.
// Adds department / designation / status / category / reporting-manager
// filters per operator requirement.
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    Plus, MagnifyingGlass, UsersThree, CaretLeft, CaretRight, ArrowRight,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { formatDate } from "../../lib/format";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import EmptyState from "../../components/EmptyState";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";

const PAGE_SIZE = 25;

const STATUS_TONE = {
    Active: "bg-emerald-100 text-emerald-700",
    Probation: "bg-amber-100 text-amber-700",
    Onboarding: "bg-sky-100 text-sky-700",
    "Notice Period": "bg-rose-100 text-rose-700",
    Resigned: "bg-rose-100 text-rose-700",
    Exited: "bg-slate-200 text-slate-600",
    Inactive: "bg-slate-200 text-slate-500",
    Draft: "bg-slate-100 text-slate-500",
};

export default function EmployeeList() {
    const { can } = useAuth();
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();

    const q = params.get("q") || "";
    const department = params.get("department") || "";
    const designation = params.get("designation") || "";
    const statusFilter = params.get("status") || "";
    const employmentCategory = params.get("category") || "";
    const reportingManagerId = params.get("rm") || "";
    const sort = params.get("sort") || "recently_added";
    const page = Math.max(1, parseInt(params.get("page") || "1", 10));

    const [searchInput, setSearchInput] = useState(q);
    const [meta, setMeta] = useState({ departments: [], designations: [], employment_categories: [], employee_statuses: [] });
    const [managers, setManagers] = useState([]);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [pages, setPages] = useState(0);
    const [loading, setLoading] = useState(true);

    const updateParam = (key, value) => {
        const next = new URLSearchParams(params);
        if (!value) next.delete(key);
        else next.set(key, value);
        if (key !== "page") next.delete("page");
        setParams(next, { replace: false });
    };

    // Load meta + managers once
    useEffect(() => {
        Promise.all([
            api.get("/hr/employee/meta"),
            api.get("/hr/employee/list", { params: { page_size: 200, sort: "name_asc" } }),
        ])
            .then(([metaRes, listRes]) => {
                setMeta(metaRes.data);
                setManagers(listRes.data.items || []);
            })
            .catch((err) => toast.error(formatApiError(err, "Failed to load filters")));
    }, []);

    useEffect(() => {
        let cancelled = false;
        api.get("/hr/employee/list", {
            params: {
                q: q || undefined,
                department: department || undefined,
                designation: designation || undefined,
                status_filter: statusFilter || undefined,
                employment_category: employmentCategory || undefined,
                reporting_manager_id: reportingManagerId || undefined,
                sort,
                page,
                page_size: PAGE_SIZE,
            },
        })
            .then(({ data }) => {
                if (cancelled) return;
                setRows(data.items || []);
                setTotal(data.total || 0);
                setPages(data.pages || 0);
            })
            .catch((err) => {
                if (!cancelled) toast.error(formatApiError(err, "Failed to load employees"));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [q, department, designation, statusFilter, employmentCategory, reportingManagerId, sort, page]);

    useEffect(() => {
        const t = setTimeout(() => {
            if (searchInput !== q) updateParam("q", searchInput);
        }, 300);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchInput, q]);

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1400px]" data-testid="employee-list-page">
            <PageHeader
                eyebrow="Employee Operations"
                title="Employees"
                description="Master directory for everyone who works with REEDOS — employees, consultants, advisors and interns."
                actions={can("employee:write") && (
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            data-testid="employee-new-shell-link"
                            onClick={() => navigate("/employees/new")}
                            className="text-xs text-slate-500 hover:text-slate-700 underline underline-offset-2"
                        >
                            Employee-only shell (no login)
                        </button>
                        {/* PFD-ONBOARDING-002 · normal new hires go through Unified
                            Onboarding (atomic user + employee + provisioning). The
                            legacy shell form stays reachable via the secondary link. */}
                        <Button data-testid="employee-new-btn" onClick={() => navigate(can("employee:onboard") ? "/admin/onboard" : "/employees/new")} className="gap-2">
                            <Plus size={16} weight="bold" /> Add Employee
                        </Button>
                    </div>
                )}
            />

            <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="employee-filter-row">
                <div className="relative flex-1 min-w-[260px] max-w-md">
                    <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                        data-testid="employee-search-input"
                        placeholder="Search code, name, email, mobile, PAN…"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        className="pl-9"
                    />
                </div>
                <select data-testid="employee-filter-department" value={department} onChange={(e) => updateParam("department", e.target.value)} className="h-9 rounded-md border border-slate-300 px-3 text-sm bg-white">
                    <option value="">All departments</option>
                    {(meta.departments || []).map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
                </select>
                <select data-testid="employee-filter-designation" value={designation} onChange={(e) => updateParam("designation", e.target.value)} className="h-9 rounded-md border border-slate-300 px-3 text-sm bg-white">
                    <option value="">All designations</option>
                    {(meta.designations || []).map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
                </select>
                <select data-testid="employee-filter-status" value={statusFilter} onChange={(e) => updateParam("status", e.target.value)} className="h-9 rounded-md border border-slate-300 px-3 text-sm bg-white">
                    <option value="">All statuses</option>
                    {(meta.employee_statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <select data-testid="employee-filter-category" value={employmentCategory} onChange={(e) => updateParam("category", e.target.value)} className="h-9 rounded-md border border-slate-300 px-3 text-sm bg-white">
                    <option value="">All categories</option>
                    {(meta.employment_categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <select data-testid="employee-filter-rm" value={reportingManagerId} onChange={(e) => updateParam("rm", e.target.value)} className="h-9 rounded-md border border-slate-300 px-3 text-sm bg-white">
                    <option value="">All reporting managers</option>
                    {managers.map((m) => <option key={m.id} value={m.id}>{m.full_name} ({m.employee_code})</option>)}
                </select>
                <select data-testid="employee-sort" value={sort} onChange={(e) => updateParam("sort", e.target.value)} className="h-9 rounded-md border border-slate-300 px-3 text-sm bg-white ml-auto">
                    <option value="recently_added">Recently added</option>
                    <option value="recently_updated">Recently updated</option>
                    <option value="name_asc">Name A → Z</option>
                    <option value="code_asc">Code A → Z</option>
                </select>
            </div>

            <div className="mt-5 rounded-xl border border-slate-200 bg-white overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="employee-table">
                        <thead className="bg-slate-50 text-slate-600">
                            <tr>
                                <th className="text-left font-medium px-4 py-3">Code</th>
                                <th className="text-left font-medium px-4 py-3">Name</th>
                                <th className="text-left font-medium px-4 py-3">Department</th>
                                <th className="text-left font-medium px-4 py-3">Designation</th>
                                <th className="text-left font-medium px-4 py-3">Category</th>
                                <th className="text-left font-medium px-4 py-3">Status</th>
                                <th className="text-left font-medium px-4 py-3">DOJ</th>
                                <th className="w-10" />
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <tr><td colSpan={8} className="text-center text-slate-400 py-12">Loading…</td></tr>}
                            {!loading && rows.length === 0 && (
                                <tr><td colSpan={8} className="py-16">
                                    <EmptyState
                                        icon={<UsersThree size={32} className="text-slate-400" />}
                                        title="No employees yet"
                                        message={can("employee:write") ? "Click 'Add Employee' to start onboarding." : "No employees have been added yet."}
                                    />
                                </td></tr>
                            )}
                            {!loading && rows.map((e) => (
                                <tr key={e.id} data-testid={`employee-row-${e.employee_code}`} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/employees/${e.id}`)}>
                                    <td className="px-4 py-3 font-mono text-xs text-slate-700">{e.employee_code}</td>
                                    <td className="px-4 py-3">
                                        <div className="font-medium text-slate-900">{e.full_name}</div>
                                        {e.official_email && <div className="text-xs text-slate-500 mt-0.5">{e.official_email}</div>}
                                    </td>
                                    <td className="px-4 py-3 text-slate-700">{e.department || <span className="text-slate-300">—</span>}</td>
                                    <td className="px-4 py-3 text-slate-700">{e.designation || <span className="text-slate-300">—</span>}</td>
                                    <td className="px-4 py-3 text-slate-700">{e.employment_category}</td>
                                    <td className="px-4 py-3">
                                        <Badge data-testid={`employee-status-${e.employee_code}`} className={`${STATUS_TONE[e.status] || "bg-slate-100 text-slate-600"} hover:bg-opacity-100`}>
                                            {e.status || "Draft"}
                                        </Badge>
                                    </td>
                                    <td className="px-4 py-3 text-xs text-slate-500">{e.date_of_joining || "—"}</td>
                                    <td className="px-4 py-3 text-right"><ArrowRight size={14} className="text-slate-400" /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {!loading && total > 0 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50/50 text-xs text-slate-600">
                        <div data-testid="employee-pagination-summary">
                            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
                        </div>
                        <div className="flex items-center gap-2">
                            <Button data-testid="employee-page-prev" size="sm" variant="outline" disabled={page <= 1} onClick={() => updateParam("page", page - 1)}><CaretLeft size={14} /></Button>
                            <span className="px-2">Page {page} of {pages}</span>
                            <Button data-testid="employee-page-next" size="sm" variant="outline" disabled={page >= pages} onClick={() => updateParam("page", page + 1)}><CaretRight size={14} /></Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
